import * as React from 'react'
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui'

import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * shadcn `RadioGroup` primitive, from the new-york registry via the CLI
 * (`npx shadcn@latest add radio-group`). Backs the `src/antd/Radio` seam's
 * shadcn half (`src/antd/Radio.shadcn.tsx`); nothing else should import it,
 * and nothing reaches it until someone flips the design toggle.
 *
 * FOUR EDITS TO THE REGISTRY OUTPUT:
 *
 * 1. The `"use client"` directive is dropped — this is a Vite SPA, not an RSC
 *    app (the same edit `src/components/ui/label.tsx` records).
 *
 * 2. THE SELECTED DOT IS A `<span>`, not lucide's `Circle`. The registry fills
 *    a `CircleIcon` with `fill-primary`; a filled disc is exactly what a
 *    `rounded-full` span already is, and routing it through the icon seam
 *    would mean inventing an antd icon name for a shape that has none.
 *    `lucide-react` may not be named outside `src/icons/antd-compat` (see
 *    `src/components/ui/checkbox.tsx`), so the choice was a span or a fake map
 *    entry. antd draws its own dot in CSS for the same reason.
 *
 * 3. forwardRef on the item — see `src/components/ui/checkbox.tsx`. Radix's
 *    RovingFocusGroup also hands the item a `tabIndex`, and a ref it cannot
 *    deliver means the seam has no element to focus.
 *
 * 4. THE ROOT DOES NOT IMPOSE A LAYOUT. The registry's `grid gap-3` stacks
 *    items vertically; antd's `Radio.Group` is inline and wraps, and 73 call
 *    sites here are written against that. The seam passes the layout it wants,
 *    so the primitive only carries `data-slot`.
 * ------------------------------------------------------------------------- */
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentProps<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      ref={ref}
      data-slot="radio-group"
      className={cn(className)}
      {...props}
    />
  )
})
RadioGroup.displayName = 'RadioGroup'

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentProps<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      data-slot="radio-group-item"
      className={cn(
        'aspect-square size-4 shrink-0 rounded-full border border-input text-primary shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:border-primary dark:bg-input/30 dark:aria-invalid:ring-destructive/40',
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <span
          data-slot="radio-group-dot"
          className="absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary"
        />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})
RadioGroupItem.displayName = 'RadioGroupItem'

export { RadioGroup, RadioGroupItem }
